import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { i18n } from '@international/mastodon-i18n';
import { hasUnfetchedCandidateReports } from 'utils/candidateReportLoad';
import useLocationQuery from 'utils/useLocationQuery';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import useAuthGateState from '../hooks/useAuthGateState';
import { fetchCandidateReports, fetchCandidateReportsIntl } from '../actions';
import { PATHS, SESSION_STORAGE_TOKEN_KEY } from '../constants';
import Loading from '../components/Loading';
import MainContainer from '../components/MainContainer';
import {
  isInternational as getIsInternational,
  isSessionStorageSupported,
} from '../lib/helpers';
import { getReturnPath } from '../utils/returnPath';
import { useInternational } from '../providers/InternationalProvider';

const getRoot = (isInternational, location) => {
  if (['#login', '#login_failed'].includes(location.hash))
    return isInternational ? PATHS.INTERNATIONAL : '/';
  if (isInternational) return PATHS.INTERNATIONAL_REPORTS_OVERVIEW;
  return PATHS.REPORTS_OVERVIEW;
};

export const AuthComponent = ({
  candidateIds,
  candidateLoggedIn,
  children,
  fetchCandidateReports: fetchReports,
  fetchCandidateReportsIntl: fetchReportsIntl,
  isInternational,
  isLoading,
  isVerification,
  portal,
  token,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useLocationQuery();
  const { error, loggedIn, loggedOut, processing, reports, reportsFetched } =
    portal;
  const isPrivacy = location.pathname.includes('/privacy');
  const hasCandidateIds = candidateIds.length > 0;
  // loggedIn (portalAuth) means reports or the non-candidate path finished.
  // candidateLogin.loggedIn with no IDs covers the Layout gap before
  // NON_CANDIDATE_LOGGED_IN.
  const isAuthenticated = loggedIn || (candidateLoggedIn && !hasCandidateIds);
  // Layout also requires a JWT and an idle fetch. Keep waiting without those:
  // login treats this session as signed in and would send it back. `error`
  // stays local so a failed token fetch does not hold the loading screen.
  const waitingForCandidateReports =
    hasUnfetchedCandidateReports({
      candidateLoggedIn,
      candidateIds,
      reportsFetched,
    }) && !error;
  const hasNewToken = Boolean(routeParams.token);
  const shouldFetch =
    !loggedOut &&
    (!reportsFetched || hasNewToken) &&
    Boolean(routeParams.mock || token) &&
    (!error || hasNewToken) &&
    !processing &&
    !candidateLoggedIn;

  // International login sets the locale before the success action. Run
  // again once that success flips loggedIn, or the tab title stays in the
  // locale from mount.
  React.useEffect(() => {
    document.title = i18n.getStr('containers.page.title');
  }, [loggedIn]);

  React.useEffect(() => {
    if (error && isSessionStorageSupported()) {
      window.sessionStorage.removeItem(SESSION_STORAGE_TOKEN_KEY);
    }
  }, [error]);

  React.useEffect(() => {
    if (
      !(processing || (!error && routeParams.mock) || isVerification) &&
      loggedIn &&
      ['/', PATHS.INTERNATIONAL].includes(location.pathname)
    ) {
      navigate(getRoot(isInternational, location), {
        replace: true,
      });
    }
  }, [
    error,
    isInternational,
    isVerification,
    location,
    loggedIn,
    navigate,
    processing,
    routeParams.mock,
  ]);

  React.useEffect(() => {
    if (!shouldFetch) return;

    if (isInternational) {
      fetchReportsIntl({ token });
    } else {
      fetchReports({ token });
    }
  }, [fetchReports, fetchReportsIntl, isInternational, shouldFetch, token]);

  if (
    !isVerification &&
    (shouldFetch || waitingForCandidateReports || processing || isLoading)
  ) {
    return (
      <MainContainer>
        <Loading data-testid='loading-reports' />
        <div className='text-muted text-center'>
          {i18n.getStr('components.searchLoadingText')}
        </div>
      </MainContainer>
    );
  }

  if (!isAuthenticated && !isVerification) {
    return (
      <Navigate
        to={`${isInternational ? '/international' : ''}${
          isPrivacy ? '/privacy' : ''
        }/login${error ? '#login_fail' : ''}`}
        state={{ prevLocation: getReturnPath(location) }}
      />
    );
  }

  return React.cloneElement(children, { reports });
};

AuthComponent.propTypes = {
  children: PropTypes.node,
  token: PropTypes.string,
  candidateIds: PropTypes.arrayOf(PropTypes.string),
  candidateLoggedIn: PropTypes.bool,
  fetchCandidateReports: PropTypes.func.isRequired,
  fetchCandidateReportsIntl: PropTypes.func.isRequired,
  portal: PropTypes.object.isRequired,
  isInternational: PropTypes.bool,
  isVerification: PropTypes.bool,
  isLoading: PropTypes.bool,
};
AuthComponent.defaultProps = {
  children: [],
  candidateIds: [],
  candidateLoggedIn: false,
  isInternational: false,
  isVerification: false,
  isLoading: false,
  token: '',
};

// loggedIn/loggedOut and the candidate session come from useAuthGateState
// (passed in as `auth`: the AuthProvider Context); report data (reports, error,
// processing, reportsFetched) stays in Redux.
const mapStateToProps = (state, { auth, international }) => {
  const { portal = {} } = state;

  return {
    portal: { ...portal, ...auth.portalAuth },
    candidateIds: auth.candidateLogin.candidateIds || [],
    candidateLoggedIn: auth.candidateLogin.loggedIn || false,
    isInternational: international || getIsInternational(),
  };
};

const actions = {
  fetchCandidateReports,
  fetchCandidateReportsIntl,
};

const ConnectedAuth = connect(mapStateToProps, actions)(AuthComponent);

const Auth = props => {
  const auth = useAuthGateState();
  const international = useInternational();
  return <ConnectedAuth {...props} auth={auth} international={international} />;
};

export default Auth;
